import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Fab,
  Tooltip,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
} from '@mui/material';
import {
  Add,
  PlayArrow,
  Stop,
  Edit,
  Delete,
  Visibility,
  Person,
  Business,
  Schedule,
  TrendingUp,
  Assignment,
  Group,
  AttachMoney,
  Dashboard,
  Timer,
  CheckCircle,
  Cancel,
  Sync,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import Layout from '../components/Layout/Layout';
import CreateProjectModal from '../components/Common/CreateProjectModal';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tasks-tabpanel-${index}`}
      aria-labelledby={`admin-tasks-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface Project {
  id: string;
  name: string;
  client: string;
  status: string;
  budget: number;
  startDate: Date;
  expectedEndDate: Date;
  actualEndDate?: Date;
  description: string;
  projectManager: string;
  teamSize: number;
  hourlyRate: number;
  tyvraPMId?: string; // Link to Tyvra PM project
}

const AdminTasks: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [activeTimer, setActiveTimer] = useState<string | null>(null);
  const [timerStartTime, setTimerStartTime] = useState<Date | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [projects, setProjects] = useState<Project[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [timeEntries, setTimeEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'project' | 'employee' | 'client' | 'time-entry'>('project');
  const [formData, setFormData] = useState<any>({});
  const [syncStatus, setSyncStatus] = useState<string>('');
  const [createProjectModalOpen, setCreateProjectModalOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);



  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (activeTimer && timerStartTime) {
      interval = setInterval(() => {
        const now = new Date();
        const elapsed = Math.floor((now.getTime() - timerStartTime.getTime()) / 1000);
        setElapsedTime(elapsed);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [activeTimer, timerStartTime]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load employees from API
      try {
        const employeesData = await apiService.getEmployees();
        console.log('Loaded employees from API:', employeesData);
        setEmployees(employeesData);
      } catch (error) {
        console.error('Failed to load employees:', error);
        // Fallback to mock data
        setEmployees([
          { id: '1', name: 'John Doe', email: 'john@company.com', role: 'Developer', status: 'active' },
          { id: '2', name: 'Jane Smith', email: 'jane@company.com', role: 'Designer', status: 'active' },
        ]);
      }
      
      // Load projects from API (use getUserProjects for regular users)
      try {
        const isAdmin = user?.role && ['root_user', 'admin', 'hr_admin', 'super_manager'].includes(user.role);
        const projectsData = isAdmin 
          ? await apiService.getProjects()
          : await apiService.getUserProjects();
        console.log('Loaded projects from API:', projectsData);
        
        // Map Tyvra PM projects to AdminTasks format
        const mappedProjects = projectsData.map(project => ({
          id: project.id,
          name: project.name,
          client: 'Unknown Client', // Tyvra PM doesn't have client field
          status: project.isActive ? 'active' : 'inactive',
          budget: 0, // Tyvra PM doesn't have budget field
          startDate: project.startDate ? new Date(project.startDate) : new Date(),
          expectedEndDate: project.endDate ? new Date(project.endDate) : new Date(),
          description: project.description || '',
          projectManager: 'Unknown', // Could be derived from leadId
          teamSize: project.teams?.length || 0,
          hourlyRate: 80, // Default rate
          tyvraPMId: project.id
        }));
        
        setProjects(mappedProjects);
      } catch (error) {
        console.error('Failed to load projects:', error);
        // Fallback to mock data
        setProjects([
          { 
            id: '1', 
            name: 'Website Redesign', 
            client: 'TechCorp', 
            status: 'active', 
            budget: 15000,
            startDate: new Date('2024-01-01'),
            expectedEndDate: new Date('2024-03-31'),
            description: 'Complete website redesign with modern UI/UX',
            projectManager: 'John Doe',
            teamSize: 5,
            hourlyRate: 75,
            tyvraPMId: 'tyvra-001'
          },
          { 
            id: '2', 
            name: 'Mobile App Development', 
            client: 'StartupXYZ', 
            status: 'active', 
            budget: 25000,
            startDate: new Date('2024-02-01'),
            expectedEndDate: new Date('2024-05-31'),
            description: 'Cross-platform mobile application development',
            projectManager: 'Jane Smith',
            teamSize: 8,
            hourlyRate: 85,
            tyvraPMId: 'tyvra-002'
          },
        ]);
      }
      
      setClients([
        { id: '1', name: 'TechCorp', email: 'contact@techcorp.com', status: 'active' },
        { id: '2', name: 'StartupXYZ', email: 'hello@startupxyz.com', status: 'active' },
      ]);
      setTimeEntries([
        { id: '1', project: 'Website Redesign', employee: 'John Doe', date: '2024-01-15', hours: 8, description: 'Frontend development' },
        { id: '2', project: 'Mobile App Development', employee: 'Jane Smith', date: '2024-01-15', hours: 6, description: 'UI/UX design' },
      ]);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleStartTimer = (projectId: string) => {
    setActiveTimer(projectId);
    setTimerStartTime(new Date());
    setElapsedTime(0);
  };

  const handleStopTimer = () => {
    setActiveTimer(null);
    setTimerStartTime(null);
    setElapsedTime(0);
    // Here you would save the time entry
  };

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleOpenDialog = (type: 'project' | 'employee' | 'client' | 'time-entry') => {
    setDialogType(type);
    setFormData({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({});
  };

  const handleSave = async () => {
    try {
      if (dialogType === 'employee') {
        // Create employee via API
        console.log('=== EMPLOYEE CREATION DEBUG ===');
        console.log('Form data:', formData);
        
        try {
          const employeeData = {
            name: formData.name,
            email: formData.email,
            position: formData.position || formData.role || 'Employee',
            employeeType: formData.employeeType || 'full-time',
            department: formData.department || 'General',
            hourlyRate: formData.hourlyRate || 0,
            phone: formData.phone || '',
            address: formData.address || '',
            skills: formData.skills || '',
            joinDate: formData.joinDate || new Date(),
            status: formData.status || 'active'
          };
          
          console.log('Sending employee data to API:', employeeData);
          
          const newEmployee = await apiService.createEmployee(employeeData);
          console.log('Employee created via API:', newEmployee);
          
          // Add to local state
          setEmployees(prevEmployees => {
            const updatedEmployees = [...prevEmployees, newEmployee];
            console.log('Updated employees array:', updatedEmployees);
            return updatedEmployees;
          });
          
          setSyncStatus('Employee created successfully!');
          setTimeout(() => setSyncStatus(''), 3000);
        } catch (error) {
          console.error('Failed to create employee via API:', error);
          setSyncStatus('Error creating employee');
          setTimeout(() => setSyncStatus(''), 3000);
        }
      } else if (dialogType === 'client') {
        // Create client
        const newClient = {
          ...formData,
          id: Date.now().toString(),
          status: 'active',
          createdAt: new Date(),
          contactPerson: formData.contactPerson || formData.name,
          phone: formData.phone || '',
          address: formData.address || ''
        };
        
        // Add to local state
        setClients([...clients, newClient]);
        
        setSyncStatus('Client created successfully!');
        setTimeout(() => setSyncStatus(''), 3000);
      } else if (dialogType === 'time-entry') {
        // Create time entry
        const newTimeEntry = {
          ...formData,
          id: Date.now().toString(),
          date: new Date(),
          employee: user?.fullName || 'Current User',
          project: formData.project || '',
          hours: parseFloat(formData.hours) || 0,
          description: formData.description || '',
          status: 'pending'
        };
        
        // Add to local state
        setTimeEntries([...timeEntries, newTimeEntry]);
        
        setSyncStatus('Time entry created successfully!');
        setTimeout(() => setSyncStatus(''), 3000);
      }
      
      handleCloseDialog();
      // Removed loadData() call as it overwrites local state
    } catch (error) {
      console.error('Failed to save:', error);
      setSyncStatus(`Error saving ${dialogType}`);
      setTimeout(() => setSyncStatus(''), 3000);
    }
  };


  const syncFromTyvraPM = async () => {
    try {
      setSyncStatus('Syncing projects from Tyvra PM...');
      const syncedProjects = await apiService.syncProjectsFromTyvraPM();
      setProjects(syncedProjects);
      setSyncStatus(`Successfully synced ${syncedProjects.length} projects from Tyvra PM!`);
      setTimeout(() => setSyncStatus(''), 3000);
    } catch (error) {
      console.error('Failed to sync projects:', error);
      setSyncStatus('Error syncing projects from Tyvra PM');
      setTimeout(() => setSyncStatus(''), 3000);
    }
  };

  const handleCreateProjectSuccess = async (project: any) => {
    setSyncStatus('Project created and synced with Tyvra PM!');
    setTimeout(() => setSyncStatus(''), 3000);
    
    // Reload projects to show the new one
    await loadData();
  };

  const handleCreateProjectError = (error: string) => {
    setSyncStatus(`Error creating project: ${error}`);
    setTimeout(() => setSyncStatus(''), 5000);
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString();
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          🕒 Admin Dashboard
        </Typography>
        
        {/* Sync Status */}
        {syncStatus && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {syncStatus}
          </Alert>
        )}
        
        {/* Active Timer Display */}
        {activeTimer && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.light', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h6">⏱️ Active Timer</Typography>
                <Typography variant="h4" sx={{ fontFamily: 'monospace' }}>
                  {formatTime(elapsedTime)}
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="error"
                startIcon={<Stop />}
                onClick={handleStopTimer}
              >
                Stop Timer
              </Button>
            </Box>
          </Paper>
        )}

        {/* Main Tabs */}
        <Paper sx={{ width: '100%' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="time tracking tabs">
            <Tab label="📊 Dashboard" />
            <Tab label="📁 Projects" />
            <Tab label="👥 Employees" />
            <Tab label="🔗 Assignments" />
            <Tab label="🏢 Clients" />
            <Tab label="⏱️ Time Entries" />
            <Tab label="💰 Billing" />
          </Tabs>

          {/* Dashboard Tab */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Active Projects
                    </Typography>
                    <Typography variant="h4">{projects.filter(p => p.status === 'active').length}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Team Members
                    </Typography>
                    <Typography variant="h4">{employees.length}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Hours This Week
                    </Typography>
                    <Typography variant="h4">
                      {timeEntries.reduce((sum, entry) => sum + entry.hours, 0)}h
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Revenue This Month
                    </Typography>
                    <Typography variant="h4">$45,250</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Projects Tab */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Projects</Typography>
              <Box>
                <Alert severity="info" sx={{ mr: 2, display: 'inline-flex' }}>
                  🔄 Automatic sync with Tyvra PM enabled
                </Alert>
                {(user?.role === 'root_user' || user?.role === 'admin' || user?.role === 'hr_admin' || user?.role === 'super_manager') && (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setCreateProjectModalOpen(true)}
                  >
                    Add Project
                  </Button>
                )}
              </Box>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Project Name</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell>Expected End</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Budget</TableCell>
                    <TableCell>Tyvra PM</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {projects.map((project) => (
                    <TableRow key={project.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2">{project.name}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {project.description}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{project.client}</TableCell>
                      <TableCell>{formatDate(project.startDate)}</TableCell>
                      <TableCell>{formatDate(project.expectedEndDate)}</TableCell>
                      <TableCell>
                        <Chip 
                          label={project.status} 
                          color={project.status === 'active' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>${project.budget.toLocaleString()}</TableCell>
                      <TableCell>
                        {project.tyvraPMId ? (
                          <Chip label="Auto-Synced" color="success" size="small" />
                        ) : (
                          <Chip label="Auto-Synced" color="success" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small">
                          <Edit />
                        </IconButton>
                        <IconButton size="small">
                          <Visibility />
                        </IconButton>
                        <IconButton size="small">
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Employees Tab */}
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">
                Employees ({employees.length})
              </Typography>
              <Box>
                {(user?.role === 'root_user' || user?.role === 'admin' || user?.role === 'hr_admin' || user?.role === 'super_manager') && (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<Sync />}
                      onClick={async () => {
                        try {
                          setSyncStatus('Syncing employees from Tyvra PM...');
                          const syncedEmployees = await apiService.syncEmployeesFromTyvraPM();
                          setEmployees(syncedEmployees);
                          setSyncStatus(`Successfully synced ${syncedEmployees.length} employees from Tyvra PM!`);
                          setTimeout(() => setSyncStatus(''), 3000);
                        } catch (error) {
                          console.error('Failed to sync employees:', error);
                          setSyncStatus('Error syncing employees from Tyvra PM');
                          setTimeout(() => setSyncStatus(''), 3000);
                        }
                      }}
                      sx={{ mr: 1 }}
                    >
                      Sync from Tyvra PM
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => handleOpenDialog('employee')}
                    >
                      Add Employee
                    </Button>
                  </>
                )}
              </Box>
            </Box>
            

            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Position</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Hourly Rate</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employees.map((employee) => (
                    <TableRow key={employee.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ mr: 2 }}>{employee.name[0]}</Avatar>
                          <Box>
                            <Typography variant="subtitle2">{employee.name}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {employee.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{employee.position || employee.role}</TableCell>
                      <TableCell>
                        <Chip 
                          label={employee.employeeType || 'full-time'} 
                          color={employee.employeeType === 'contractor' ? 'warning' : 'primary'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{employee.department || 'General'}</TableCell>
                      <TableCell>${employee.hourlyRate || 0}/hr</TableCell>
                      <TableCell>
                        <Chip 
                          label={employee.status} 
                          color={employee.status === 'active' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small">
                          <Edit />
                        </IconButton>
                        <IconButton size="small">
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Project Assignments Tab */}
          <TabPanel value={tabValue} index={3}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Project Assignments</Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => window.location.href = '/project-assignments'}
              >
                Manage Assignments
              </Button>
            </Box>
            <Typography color="textSecondary" sx={{ mb: 2 }}>
              Assign employees to projects with specific roles. Click "Manage Assignments" to view and manage all project assignments.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Quick Stats
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total Assignments: {projects.reduce((sum, project) => sum + (project.teamSize || 0), 0)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Active Projects: {projects.filter(p => p.status === 'active').length}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Available Employees: {employees.length}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Recent Assignments
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      No recent assignments to display.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Clients Tab */}
          <TabPanel value={tabValue} index={4}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Clients</Typography>
              {(user?.role === 'root_user' || user?.role === 'admin' || user?.role === 'hr_admin' || user?.role === 'super_manager') && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => handleOpenDialog('client')}
                >
                  Add Client
                </Button>
              )}
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Client</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Industry</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clients.map((client) => (
                    <TableRow key={client.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2">{client.name}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {client.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{client.contactPerson || client.name}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {client.phone || 'No phone'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={client.clientType || 'corporate'} 
                          color="primary"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{client.industry || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={client.status} 
                          color={client.status === 'active' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small">
                          <Edit />
                        </IconButton>
                        <IconButton size="small">
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Time Entries Tab */}
          <TabPanel value={tabValue} index={5}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Time Entries</Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenDialog('time-entry')}
              >
                Add Time Entry
              </Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell>Employee</TableCell>
                    <TableCell>Hours</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {timeEntries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>{entry.date}</TableCell>
                      <TableCell>{entry.project}</TableCell>
                      <TableCell>{entry.employee}</TableCell>
                      <TableCell>{entry.hours}h</TableCell>
                      <TableCell>{entry.description}</TableCell>
                      <TableCell>
                        <IconButton size="small">
                          <Edit />
                        </IconButton>
                        <IconButton size="small">
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Billing Tab */}
          <TabPanel value={tabValue} index={6}>
            <Typography variant="h6" gutterBottom>Billing & Invoicing</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Recent Invoices</Typography>
                    <List>
                      <ListItem>
                        <ListItemText
                          primary="Invoice #001"
                          secondary="TechCorp - Website Redesign"
                        />
                        <Chip label="$15,000" color="success" />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Invoice #002"
                          secondary="StartupXYZ - Mobile App"
                        />
                        <Chip label="$25,000" color="success" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Revenue Overview</Typography>
                    <Typography variant="h4" color="primary">$40,000</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total revenue this month
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
        </Paper>

        {/* Floating Action Button for Quick Timer */}
        <Fab
          color="primary"
          aria-label="start timer"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={() => handleStartTimer('quick')}
        >
          <PlayArrow />
        </Fab>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {dialogType === 'employee' && 'Add Employee'}
            {dialogType === 'client' && 'Add Client'}
            {dialogType === 'time-entry' && 'Add Time Entry'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              {dialogType === 'employee' && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Employee Name"
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={formData.email || ''}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Employee Type</InputLabel>
                      <Select
                        value={formData.employeeType || 'full-time'}
                        onChange={(e) => setFormData({ ...formData, employeeType: e.target.value })}
                        label="Employee Type"
                      >
                        <MenuItem value="full-time">Full Time</MenuItem>
                        <MenuItem value="part-time">Part Time</MenuItem>
                        <MenuItem value="contractor">Contractor</MenuItem>
                        <MenuItem value="freelancer">Freelancer</MenuItem>
                        <MenuItem value="intern">Intern</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Position/Title"
                      value={formData.position || ''}
                      onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Department"
                      value={formData.department || ''}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Hourly Rate ($)"
                      type="number"
                      value={formData.hourlyRate || ''}
                      onChange={(e) => setFormData({ ...formData, hourlyRate: parseFloat(e.target.value) })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      value={formData.phone || ''}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DatePicker
                      label="Join Date"
                      value={formData.joinDate || null}
                      onChange={(date) => setFormData({ ...formData, joinDate: date })}
                      slotProps={{ textField: { fullWidth: true, required: true } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={formData.status || 'active'}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        label="Status"
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                        <MenuItem value="terminated">Terminated</MenuItem>
                        <MenuItem value="on-leave">On Leave</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      multiline
                      rows={2}
                      value={formData.address || ''}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Skills/Notes"
                      multiline
                      rows={3}
                      value={formData.skills || ''}
                      onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
                      placeholder="Enter skills, certifications, or additional notes"
                    />
                  </Grid>
                </Grid>
              )}
              {dialogType === 'client' && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Client Name"
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={formData.email || ''}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Contact Person"
                      value={formData.contactPerson || ''}
                      onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      value={formData.phone || ''}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Company Website"
                      value={formData.website || ''}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Client Type</InputLabel>
                      <Select
                        value={formData.clientType || 'corporate'}
                        onChange={(e) => setFormData({ ...formData, clientType: e.target.value })}
                        label="Client Type"
                      >
                        <MenuItem value="corporate">Corporate</MenuItem>
                        <MenuItem value="startup">Startup</MenuItem>
                        <MenuItem value="government">Government</MenuItem>
                        <MenuItem value="non-profit">Non-Profit</MenuItem>
                        <MenuItem value="individual">Individual</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={formData.status || 'active'}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        label="Status"
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                        <MenuItem value="prospect">Prospect</MenuItem>
                        <MenuItem value="former">Former Client</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Industry"
                      value={formData.industry || ''}
                      onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      multiline
                      rows={3}
                      value={formData.address || ''}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Notes"
                      multiline
                      rows={3}
                      value={formData.notes || ''}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Additional notes about the client"
                    />
                  </Grid>
                </Grid>
              )}
              {dialogType === 'time-entry' && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      select
                      label="Project"
                      value={formData.project || ''}
                      onChange={(e) => setFormData({ ...formData, project: e.target.value })}
                    >
                      {projects.map((project) => (
                        <MenuItem key={project.id} value={project.name}>
                          {project.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Hours"
                      type="number"
                      value={formData.hours || ''}
                      onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      multiline
                      rows={3}
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                  </Grid>
                </Grid>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSave} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create Project Modal */}
        <CreateProjectModal
          open={createProjectModalOpen}
          onClose={() => setCreateProjectModalOpen(false)}
          onSuccess={handleCreateProjectSuccess}
          onError={handleCreateProjectError}
        />
      </Box>
    </Layout>
  );
};

export default AdminTasks; 